import React, { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Shield, Users, Bell, Coins, Home, Settings, Activity, FileBarChart2, LifeBuoy, LineChart, Database, GaugeCircle, UserCircle2 } from 'lucide-react'
import AlertasNormativas from '../components/AlertasNormativas'
import UserManagement from '../components/UserManagement'
import NormativeTimeline from '../components/admin/NormativeTimeline'
import TicketsInbox from '../components/admin/TicketsInbox'
import SupportAuditLog from '../components/admin/SupportAuditLog'
import AnalyticsDashboard from '../components/admin/AnalyticsDashboard'
import NormativeGovernancePanel from '../components/admin/NormativeGovernancePanel'
import IndicesGovernancePanel from '../components/admin/IndicesGovernancePanel'
import DashboardIntegridad from '../components/admin/DashboardIntegridad'

export default function AdminMasterPanel() {
  const [section, setSection] = useState('war-room')
  const sections = useMemo(() => ([
    { id: 'war-room', label: 'War Room' },
    { id: 'iam', label: 'IAM Usuarios' },
    { id: 'normative-governance', label: 'Gobernanza Normativa' },
    { id: 'indices-governance', label: 'Gobernanza Indices' },
    { id: 'normative', label: 'Historial Normativo' },
    { id: 'tickets', label: 'Tickets' },
    { id: 'support-audit', label: 'Auditoria Soporte' },
    { id: 'analytics', label: 'Analytics' },
    { id: 'integridad', label: 'Dashboard Integridad' },
  ]), [])

  const helpBySection = useMemo(() => ({
    'war-room': {
      title: 'War Room',
      detail: 'Monitorea alertas operativas y novedades urgentes. Impacto: detecta riesgos inmediatos sobre calculos y dictamenes antes de aprobar cambios.',
      actions: ['Revisar alertas con confianza alta', 'Descartar ruido de fuentes bajas', 'Escalar novedades criticas a gobernanza normativa'],
    },
    iam: {
      title: 'IAM Usuarios',
      detail: 'Administra roles, permisos y ajustes de coins desde un solo punto. Impacto: centraliza operaciones criticas sobre usuarios y evita dobles paneles.',
      actions: ['Buscar usuario por email o UID', 'Ajustar coins con motivo trazable', 'Auditar cambios de rol y permisos'],
    },
    'normative-governance': {
      title: 'Gobernanza Normativa',
      detail: 'Controla el ciclo de vida de versiones legales: draft, reviewed, approved, activated, rejected y rollback. Impacto: evita cambios no validados en el motor.',
      actions: ['Publicar versiones en draft', 'Aprobar y activar solo cambios justificados', 'Usar rollback ante incidentes'],
    },
    'indices-governance': {
      title: 'Gobernanza Indices',
      detail: 'Valida series economicas con revision humana antes de afectar calculos. Impacto: evita contaminacion semantica y coeficientes absurdos en dictamenes.',
      actions: ['Cargar lote en staging', 'Revisar alertas por periodo y aprobar con evidencia', 'Publicar y activar version solo aprobada'],
    },
    normative: {
      title: 'Historial Normativo',
      detail: 'Consulta linea temporal de actualizaciones normativas y aplicacion previa. Impacto: trazabilidad legal de decisiones y dictamenes emitidos.',
      actions: ['Corroborar fuente oficial', 'Comparar impactos por fecha', 'Verificar aprobador y estado'],
    },
    tickets: {
      title: 'Tickets',
      detail: 'Gestiona incidencias de usuarios y soporte funcional. Impacto: reduce fallos de operacion y mejora continuidad del servicio.',
      actions: ['Priorizar tickets bloqueantes', 'Responder con evidencia tecnica', 'Cerrar tickets con solucion verificable'],
    },
    'support-audit': {
      title: 'Auditoria Soporte',
      detail: 'Revisa trazas y acciones administrativas en soporte. Impacto: control de integridad operacional y cumplimiento interno.',
      actions: ['Filtrar por admin y ticket', 'Detectar patrones de riesgo', 'Documentar desvio y correccion'],
    },
    analytics: {
      title: 'Analytics',
      detail: 'Mide salud de uso, conversion y performance funcional. Impacto: prioriza mejoras sobre evidencia y no por intuicion.',
      actions: ['Revisar metricas diarias', 'Detectar caidas de flujo', 'Planificar mejoras por impacto real'],
    },
    integridad: {
      title: 'Dashboard Integridad',
      detail: 'Verifica que el motor de cálculo produce los resultados esperados. Impacto: detecta regresiones silenciosas al comparar contra 15 casos testigo con valores golden.',
      actions: ['Ejecutar simulación completa', 'Revisar campos en rojo por caso', 'Confirmar 100% antes de cambios normativos'],
    },
  }), [])

  const contextualHelp = helpBySection[section] || helpBySection['war-room']

  const navGroups = useMemo(() => ([
    {
      title: 'Gestion Comercial',
      items: [
        { id: 'iam', label: 'Usuarios', icon: Users },
        { id: 'tickets', label: 'Monedas y Soporte', icon: Coins },
        { id: 'analytics', label: 'Suscripciones y Uso', icon: LineChart },
      ],
    },
    {
      title: 'Herramientas del Sistema',
      items: [
        { id: 'war-room', label: 'Metricas de Servidor', icon: GaugeCircle },
        { id: 'indices-governance', label: 'Configuracion de Indices', icon: Database },
        { id: 'support-audit', label: 'Logs y Auditoria', icon: FileBarChart2 },
        { id: 'normative-governance', label: 'Gobernanza Normativa', icon: Settings },
        { id: 'normative', label: 'Historial Normativo', icon: Activity },
        { id: 'integridad', label: 'Dashboard Integridad', icon: GaugeCircle },
      ],
    },
  ]), [])

  return (
    <div className="admin-theme-scope min-h-screen bg-[#14161d] text-white">
      <div className="mx-auto flex max-w-[1600px] gap-5 px-4 py-5 lg:px-6">
        <aside className="w-[300px] shrink-0 rounded-2xl border border-white/10 bg-[#1f2128] p-4 shadow-[0_22px_60px_rgba(0,0,0,0.42)]">
          <div className="mb-4 flex items-center gap-2 border-b border-white/10 pb-4">
            <Shield className="h-5 w-5 text-cyan-300" />
            <h1 className="text-sm font-bold tracking-wide text-white">ADMIN MASTER</h1>
          </div>

          <div className="space-y-5">
            {navGroups.map((group) => (
              <div key={group.title}>
                <p className="mb-2 px-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#9e9e9e]">{group.title}</p>
                <nav className="space-y-1.5 text-sm">
                  {group.items.map((item) => {
                    const Icon = item.icon
                    return (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => setSection(item.id)}
                        className={`flex w-full items-center gap-2 rounded-xl border px-3 py-2.5 text-left transition ${
                          section === item.id
                            ? 'border-cyan-300/45 bg-cyan-500/14 text-cyan-200'
                            : 'border-white/10 bg-[#24262d] text-[#c9c9c9] hover:border-cyan-300/20 hover:text-white'
                        }`}
                      >
                        <Icon className="h-4 w-4" />
                        <span className="font-medium">{item.label}</span>
                      </button>
                    )
                  })}
                </nav>
              </div>
            ))}
          </div>

          <div className="mt-5 rounded-xl border border-white/10 bg-[#24262d] p-3">
            <div className="flex items-center gap-2">
              <UserCircle2 className="h-8 w-8 text-cyan-300" />
              <div>
                <p className="text-xs text-[#9e9e9e]">Administrador</p>
                <p className="text-sm font-semibold text-white">Sesion Protegida</p>
              </div>
            </div>
          </div>

          <Link to="/" className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-xl border border-cyan-300/35 bg-cyan-500/12 px-3 py-2 text-xs font-semibold text-cyan-100 hover:bg-cyan-500/20">
            <Home className="h-4 w-4" /> Volver a Calculadora
          </Link>
        </aside>

        <main className="min-w-0 flex-1 space-y-4">
          <div className="rounded-2xl border border-white/10 bg-[#1f2128] p-4 shadow-[0_16px_44px_rgba(0,0,0,0.35)]">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h2 className="text-xl font-bold text-white">Centro de Control Premium</h2>
                <p className="mt-1 text-sm text-[#9e9e9e]">Modo administrador activo. Gestiona normativa, usuarios y operaciones.</p>
              </div>
              <div className="inline-flex items-center gap-2 rounded-lg border border-emerald-300/25 bg-emerald-500/10 px-3 py-1.5 text-xs font-semibold text-emerald-300">
                <Activity className="h-3.5 w-3.5" /> Online
              </div>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <div className="rounded-xl border border-cyan-300/20 bg-[#24262d] p-4">
              <div className="flex items-center justify-between">
                <p className="text-xs uppercase tracking-wide text-[#9e9e9e]">Total de Usuarios</p>
                <Users className="h-4 w-4 text-cyan-300" />
              </div>
              <p className="mt-3 text-2xl font-bold text-white">IAM</p>
              <div className="mt-2 h-1.5 rounded-full bg-cyan-500/15"><div className="h-1.5 w-3/4 rounded-full bg-cyan-300" /></div>
            </div>
            <div className="rounded-xl border border-emerald-300/20 bg-[#24262d] p-4">
              <div className="flex items-center justify-between">
                <p className="text-xs uppercase tracking-wide text-[#9e9e9e]">Coins Vendidos</p>
                <Coins className="h-4 w-4 text-emerald-300" />
              </div>
              <p className="mt-3 text-2xl font-bold text-white">Finanzas</p>
              <div className="mt-2 h-1.5 rounded-full bg-emerald-500/15"><div className="h-1.5 w-2/3 rounded-full bg-emerald-300" /></div>
            </div>
            <div className="rounded-xl border border-amber-300/20 bg-[#24262d] p-4">
              <div className="flex items-center justify-between">
                <p className="text-xs uppercase tracking-wide text-[#9e9e9e]">Dictamenes Generados</p>
                <FileBarChart2 className="h-4 w-4 text-amber-300" />
              </div>
              <p className="mt-3 text-2xl font-bold text-white">Analytics</p>
              <div className="mt-2 h-1.5 rounded-full bg-amber-500/15"><div className="h-1.5 w-4/5 rounded-full bg-amber-300" /></div>
            </div>
            <div className="rounded-xl border border-rose-300/20 bg-[#24262d] p-4">
              <div className="flex items-center justify-between">
                <p className="text-xs uppercase tracking-wide text-[#9e9e9e]">Errores del Sistema</p>
                <Bell className="h-4 w-4 text-rose-300" />
              </div>
              <p className="mt-3 text-2xl font-bold text-white">War Room</p>
              <div className="mt-2 h-1.5 rounded-full bg-rose-500/15"><div className="h-1.5 w-1/2 rounded-full bg-rose-300" /></div>
            </div>
          </div>

          <div className="grid gap-4 xl:grid-cols-3">
            <section className="rounded-2xl border border-white/10 bg-[#1f2128] p-4 shadow-[0_16px_44px_rgba(0,0,0,0.35)] xl:col-span-2">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-sm font-semibold text-white">Modulo Operativo</h3>
                <span className="rounded-full border border-cyan-300/25 bg-cyan-500/10 px-2 py-0.5 text-[11px] font-semibold text-cyan-200">{(sections.find((s) => s.id === section) || {}).label || 'Panel'}</span>
              </div>
            {section === 'war-room' && <AlertasNormativas />}
            {section === 'iam' && <UserManagement />}
            {section === 'normative-governance' && <NormativeGovernancePanel />}
            {section === 'indices-governance' && <IndicesGovernancePanel />}
            {section === 'normative' && <NormativeTimeline />}
            {section === 'tickets' && <TicketsInbox />}
            {section === 'support-audit' && <SupportAuditLog />}
            {section === 'analytics' && <AnalyticsDashboard />}
            {section === 'integridad' && <DashboardIntegridad />}
            </section>

            <section className="rounded-2xl border border-white/10 bg-[#1f2128] p-4 shadow-[0_16px_44px_rgba(0,0,0,0.35)]">
              <div className="mb-3 flex items-center gap-2">
                <LifeBuoy className="h-4 w-4 text-cyan-300" />
                <h3 className="text-sm font-semibold text-white">Pulso Administrativo</h3>
              </div>
              <div className="space-y-3">
                <div className="rounded-lg border border-cyan-300/20 bg-cyan-500/8 p-3">
                  <p className="text-xs text-[#9e9e9e]">Operacion General</p>
                  <p className="mt-1 text-lg font-bold text-cyan-200">Estable</p>
                </div>
                <div className="rounded-lg border border-amber-300/20 bg-amber-500/8 p-3">
                  <p className="text-xs text-[#9e9e9e]">Cola de Revision</p>
                  <p className="mt-1 text-lg font-bold text-amber-200">Moderada</p>
                </div>
                <div className="rounded-lg border border-rose-300/20 bg-rose-500/8 p-3">
                  <p className="text-xs text-[#9e9e9e]">Eventos Criticos</p>
                  <p className="mt-1 text-lg font-bold text-rose-200">Monitoreado</p>
                </div>
              </div>
            </section>
          </div>

        </main>

        <aside className="hidden w-80 2xl:block">
          <div className="sticky top-5 rounded-2xl border border-white/10 bg-[#1f2128] p-4 shadow-[0_18px_48px_rgba(0,0,0,0.35)]">
            <p className="text-xs font-semibold uppercase tracking-wide text-cyan-300">Guia Operativa</p>
            <h3 className="mt-1 text-sm font-bold text-white">{contextualHelp.title}</h3>
            <p className="mt-2 text-xs leading-5 text-[#9e9e9e]">{contextualHelp.detail}</p>
            <div className="mt-3 space-y-2">
              {contextualHelp.actions.map((item, idx) => (
                <p key={`${section}-tip-${idx}`} className="rounded-lg border border-white/10 bg-[#24262d] px-2 py-1.5 text-[11px] text-[#d4d4d4]">- {item}</p>
              ))}
            </div>
          </div>
        </aside>
      </div>
    </div>
  )
}
